import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, ArrowLeft, CheckCircle2, MapPin, RefreshCw } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { formatLookupValues } from "@/utils/staffingUtil";
import { Field, MatchRow, OpeningBadges, OpeningInitials, OpeningsBlockerAlert } from "./openingsUi";
import { getInterestErrorMessage, useOpeningDetail } from "../hooks/useOpenings";

/**
 * Single opening view for the embedded mobile app. The opening is identified by
 * the `postingId` query string parameter (e.g.
 * #/embedded-app/openings/detail?postingId=49642); `id` is accepted as an alias.
 * The match itself is fetched for the signed in staff member from
 * staffing/matches/postings/{postingId}/staff.
 *
 * When `loginName` is supplied the route is a standalone deep link to someone
 * else's match - navigating away again is the host app's job, so there is no
 * back button. Without it the viewer came from the openings list, so a back
 * button returns there.
 */
const OpeningDetail = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const rawId = searchParams.get('postingId') ?? searchParams.get('id');
    const postingId = rawId !== null && rawId.trim() !== '' && !Number.isNaN(Number(rawId))
        ? Number(rawId)
        : null;

    // Optional override of whose match to show, e.g. ?loginName=positive\calnolan
    const loginName = searchParams.get('loginName') ?? undefined;

    const { opening, loading, error, blocker, refresh, expressInterest } = useOpeningDetail(postingId, loginName);

    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitted, setSubmitted] = useState(false);

    const onExpressInterest = async () => {
        if (!opening) return;
        setSubmitting(true);
        setSubmitError(null);
        try {
            await expressInterest(opening);
            setSubmitted(true);
        } catch (err) {
            setSubmitError(getInterestErrorMessage(err));
        } finally {
            setSubmitting(false);
        }
    };

    /** Back to the list, keeping host supplied params (darkTheme, ...). */
    const backToList = () => {
        const params = new URLSearchParams(searchParams);
        params.delete('postingId');
        params.delete('id');
        const query = params.toString();
        navigate(`/embedded-app/openings${query ? `?${query}` : ''}`);
    };

    const header = (
        <div className="flex items-center gap-2">
            {!loginName && (
                <Button variant="ghost" size="sm" onClick={backToList} aria-label="Back to openings">
                    <ArrowLeft className="h-4 w-4" />
                </Button>
            )}
            <h1 className="text-lg font-semibold">Opening</h1>
        </div>
    );

    if (postingId === null) {
        return (
            <div className="mx-auto flex w-full max-w-xl flex-col gap-3 p-3">
                {header}
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Missing posting id</AlertTitle>
                    <AlertDescription>Add a <code>postingId</code> parameter to the url, e.g. <code>?postingId=49642</code>.</AlertDescription>
                </Alert>
            </div>
        );
    }

    return (
        <div className="mx-auto flex w-full max-w-xl flex-col gap-3 p-3 pb-8">
            {header}

            {error && (
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Couldn't load this opening</AlertTitle>
                    <AlertDescription className="flex flex-col items-start gap-2">
                        <span>{error}</span>
                        <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
                            <RefreshCw className="mr-2 h-3 w-3" /> Retry
                        </Button>
                    </AlertDescription>
                </Alert>
            )}

            <OpeningsBlockerAlert blocker={blocker} />

            {loading && (
                <Card>
                    <CardContent className="flex flex-col gap-4 p-4">
                        <div className="flex items-center gap-3">
                            <Skeleton className="h-12 w-12 rounded-full" />
                            <div className="flex-1 space-y-2">
                                <Skeleton className="h-5 w-40" />
                                <Skeleton className="h-3 w-28" />
                            </div>
                        </div>
                        <Skeleton className="h-2 w-full" />
                        <Skeleton className="h-24 w-full" />
                    </CardContent>
                </Card>
            )}

            {!loading && !error && !blocker && !opening && (
                <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Opening not found</AlertTitle>
                    <AlertDescription>
                        No match for posting #{postingId}. It may have been staffed or
                        dropped out of your matches.
                    </AlertDescription>
                </Alert>
            )}

            {!loading && opening && (
                <>
                    <Card>
                        <CardContent className="flex flex-col gap-4 p-4">
                            <div className="flex items-start gap-3">
                                <OpeningInitials clientName={opening.clientName} className="h-12 w-12 text-base" />
                                <div className="min-w-0 flex-1">
                                    <div className="text-base font-semibold leading-tight">
                                        {opening.postingID != null ? `Opening #${opening.postingID}` : 'Potential match'}
                                    </div>
                                    <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                                        <MapPin className="h-3 w-3 shrink-0" />
                                        <span className="truncate">
                                            {opening.parentCaregiver1City ?? 'City unknown'} &middot; {opening.distance.toFixed(1)} mi away
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <OpeningBadges opening={opening} />

                            <Separator />

                            <div className="flex flex-col gap-2">
                                <div className="text-sm font-semibold">Match breakdown</div>
                                <MatchRow label="Overall" percent={opening.percentMatch} />
                                <MatchRow label="Availability" percent={opening.percentMatchAvail} />
                                <MatchRow label="Distance" percent={opening.percentMatchDistance} />
                                <MatchRow label="Credentials" percent={opening.percentMatchCredentials} />
                            </div>

                            <Separator />

                            <div className="grid grid-cols-2 gap-x-3 gap-y-3">
                                <Field label="Age" value={opening.age} />
                                <Field label="Gender" value={opening.gender} />
                                <Field label="Client Status" value={opening.clientStatus} />
                                <Field label="Enrollment" value={formatLookupValues(opening.enrollmentStatus)} />
                                <Field label="Language" value={formatLookupValues(opening.clientLang)} />
                                <Field label="County" value={opening.parentCaregiver1County} />
                                <Field label="Funding Source" value={formatLookupValues(opening.insuranceCompany)} />
                                <Field label="Funding Type" value={formatLookupValues(opening.fundingSourceTypes)} />
                            </div>
                        </CardContent>
                    </Card>

                    {submitError && (
                        <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertTitle>Couldn't express interest</AlertTitle>
                            <AlertDescription>{submitError}</AlertDescription>
                        </Alert>
                    )}

                    {submitted && (
                        <Alert>
                            <CheckCircle2 className="h-4 w-4" />
                            <AlertTitle>Interest sent</AlertTitle>
                            <AlertDescription>
                                Your regional team has been notified about this opening.
                            </AlertDescription>
                        </Alert>
                    )}

                    {opening.processRowID == null && (
                        <Button
                            className="w-full"
                            size="lg"
                            disabled={submitting || submitted || opening.postingID == null}
                            onClick={onExpressInterest}
                        >
                            {submitting ? 'Sending...'
                                : submitted ? 'Interest sent'
                                    : 'Express interest'}
                        </Button>
                    )}
                </>
            )}
        </div>
    );
};

export default OpeningDetail;
